import { useRef, useCallback, useEffect } from "react";
/**
 * Returns a function which, when called, will set a timeout that invokes the given callback
 * after the provided number of milliseconds. This is reactive, so the most up-to-date version
 * of `callback` that has been passed into this function is what will be invoked by the timeout
 * when it executes. The return of value this function is an object which has a cancel property,
 * which is a function that when called will cancel the delayed callback if pending.
 *
 * Any timeouts that have not executed before the component is unmounted will automatically
 * be canceled when the component unmounts.
 */ export var useDelayedCallback = function(callback) {
    var callbackByRef = useRef(callback);
    useEffect(function() {
        callbackByRef.current = callback;
    }, [
        callbackByRef,
        callback
    ]);
    var onDispatch = useCallback(function() {
        var _callbackByRef_current;
        (_callbackByRef_current = callbackByRef.current) === null || _callbackByRef_current === void 0 ? void 0 : _callbackByRef_current.call(callbackByRef);
    }, [
        callbackByRef
    ]);
    var dispatches = useRef(new Set());
    var addDispatch = useCallback(function(delay) {
        var timeout = setTimeout(function() {
            onDispatch();
            dispatches.current.delete(timeout);
        }, delay);
        dispatches.current.add(timeout);
        var cancelDispatch = function() {
            clearTimeout(timeout);
            dispatches.current.delete(timeout);
        };
        return {
            cancel: cancelDispatch
        };
    }, [
        dispatches,
        onDispatch
    ]);
    useEffect(function() {
        var dispatchesToCleanup = dispatches.current;
        return function() {
            return dispatchesToCleanup.forEach(function(timeout) {
                return clearTimeout(timeout);
            });
        };
    }, [
        dispatches
    ]);
    return addDispatch;
};
